/**
 * The one structured logger for Cloudflare Worker code — every app's Worker
 * and `@bindery/auth-consumer` write through this.
 *
 * A Worker has no process to hold a backend singleton for, and the platform
 * already has a log stream: one `console.log` line per event, which Workers
 * observability / Logpush forward to Axiom. So this is not a `Logger` with a
 * backend; it is the line format, written once.
 *
 * ## Why it exists
 *
 * Seven Workers each hand-rolled `console.log(JSON.stringify({...}))`, and
 * the copies drifted (code review 2026-09-30, 07 #5 / 08 #19): four put a
 * failure's `error` at the top level, checkout nested it as `data.error`, one
 * added a top-level `userId`, one had no error slot at all, and only some of
 * them checked the event name. An Axiom query on `error.message` silently
 * missed every checkout failure. One emitter makes the wire shape a property
 * of the package instead of a convention each copy has to remember.
 *
 * ## The wire shape
 *
 * `{ service, eventName, level, userId?, data?, error? }` — a strict subset of
 * the canonical `AxiomEvent` (./axiom-event.ts), with every key named, typed
 * and placed exactly as there. So `error` is the closed three-key `LogError`
 * at the TOP level, never inside `data`, and a query written against browser
 * or Convex events (`['error']['message']`, `['userId']`) matches Worker
 * events unchanged. The canonical event's `eventId`, `_time`, `timestamp` and
 * `environment` are not stamped: the platform's own log envelope carries the
 * timestamp, and a Worker module has no build-mode signal to put in
 * `environment` without reading env, which this package never does.
 *
 * Payload keys stay inside `data` (an Axiom map field), so they never spend
 * the dataset's top-level field budget (logging-strategy §12).
 *
 * ## What it refuses
 *
 * An event name that is not `domain.action`-shaped (`EVENT_NAME_PATTERN`, the
 * same check the browser ingest boundary applies) throws. Every name in the
 * workspace is a literal, so a bad one is a developer error, and it should
 * fail the test that reaches it rather than quietly produce an event no
 * dashboard query can find. This is the only throw: serialization falls back
 * the way `serializeEvents` does, so an unserializable `data` costs the bag,
 * not the event.
 *
 * Not in the package barrel — the barrel is what the browser bundles. Import
 * it by subpath: `@bindery/logging/worker-logger`.
 */
import { type AxiomEvent, serializeEvents } from "./axiom-event";
import { EVENT_NAME_PATTERN } from "./ingest-limits";
import type { EventName, LogError, LogLevel } from "./types";

/** Worker events are `info` or `error`; there is no debug gate to honor. */
export type WorkerLogLevel = Exclude<LogLevel, "debug">;

/** One emitted line: the `AxiomEvent` keys a Worker can truthfully fill. */
export type WorkerLogLine<Data extends object> = Pick<
  AxiomEvent,
  "service" | "eventName" | "level" | "userId" | "error"
> & { data?: Data };

export interface WorkerLogOptions {
  /**
   * Severity. Defaults to `"error"` when an `error` is passed and `"info"`
   * otherwise; pass it for an error-level event that has no `LogError` to
   * carry (a refused request, a misconfiguration).
   */
  level?: WorkerLogLevel;
  /**
   * The authenticated subject — an opaque id, never an email or name
   * (logging-strategy §3/§4). Top-level, as on every canonical event.
   */
  userId?: string;
}

export interface WorkerLogger<Data extends object = Record<string, unknown>> {
  /**
   * Emit one structured event on the Worker's native log stream.
   *
   * @param eventName - `domain.action` (e.g. `auth.exchange.consume`).
   * @param data - Event-specific fields. No PII, no credentials.
   * @param error - The closed three-key error; omit on success.
   * @param options - Level override and the authenticated subject.
   */
  write(
    eventName: EventName,
    data?: Data,
    error?: LogError,
    options?: WorkerLogOptions,
  ): void;
}

/**
 * A logger bound to one `service` discriminator — the field every query on a
 * shared dataset leads with.
 *
 * Generic over `Data` so a caller can narrow what a call site may log to an
 * allow-list TYPE (checkout's `CheckoutLogFields` is the case: a call site
 * physically cannot pass cart contents or a receipt JWS).
 */
export function createWorkerLogger<
  Data extends object = Record<string, unknown>,
>(service: string): WorkerLogger<Data> {
  return {
    write(eventName, data, error, options) {
      if (!EVENT_NAME_PATTERN.test(eventName)) {
        throw new Error(
          `Event name must use domain.action format (e.g. "${service}.request.complete"), got: "${eventName}"`,
        );
      }
      const line: WorkerLogLine<Data> = {
        service,
        eventName,
        level: options?.level ?? (error === undefined ? "info" : "error"),
        ...(options?.userId === undefined ? {} : { userId: options.userId }),
        ...(data === undefined ? {} : { data }),
        // Enumerated key by key, as `toAxiomEvent` does: a caller's error
        // object with extra keys must not leak them into the dataset schema.
        ...(error === undefined
          ? {}
          : {
              error: {
                type: error.type,
                message: error.message,
                ...(error.code === undefined ? {} : { code: error.code }),
              },
            }),
      };
      // One object, not an array: the stream reads one event per line.
      console.log(serializeEvents([line]).slice(1, -1));
    },
  };
}
